import React, { useCallback, useContext, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import VerticalDots from 'mdi-react/DotsVerticalIcon';
import Dropdown from '@prisjakt/ui-dropdown-new';
import GoToStoreButton from '../../components/go-to-store-button';
import PriceColumn from '../../components/price-column/price-column';
import CampaignBadge from '../../../../../../../components/campaign-badge';
import ReportModal from '../../components/report-button/report-modal';
import * as Table from '../styles';
import * as S from './styles';
import { IAlternativePriceRowProps } from './types';

const AlternativePriceRow = ({
  campaign,
  externalUri,
  onClickOut,
  isInternationalPrice,
  price,
  priceStore,
  productImage,
  tableLinkProps,
  priceDisplay,
}: IAlternativePriceRowProps) => {
  const { t } = useContext(AppContext);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const featured = priceStore && priceStore.featured;

  const openModal = useCallback(
    (e: React.MouseEvent) => {
      // Stops the PriceRow link and onClick from firing
      e.preventDefault();
      e.stopPropagation();

      setModalIsOpen(true);
    },
    [setModalIsOpen]
  );

  const closeModal = useCallback(() => setModalIsOpen(false), [setModalIsOpen]);

  const handleClickOut = useCallback(
    (e: React.MouseEvent) => onClickOut(e, price.id),
    [price.id, onClickOut]
  );

  const renderProductNameColumn = () => (
    <Table.Column expand>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        vertical
        alignLeft
      >
        <S.ProductName light={!featured}>{price.name}</S.ProductName>
      </Table.Link>
    </Table.Column>
  );

  const renderStockStatusColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignCenter
      >
        {featured && (
          <S.StockStatusColumn
            status={
              price.stock && price.stock.status ? price.stock.status : undefined
            }
            statusText={
              price.stock && price.stock.statusText
                ? price.stock.statusText
                : undefined
            }
            countryCode={priceStore && priceStore.countryCode}
            toneDown={!featured}
            isInternationalPrice={isInternationalPrice}
          />
        )}
      </Table.Link>
    </Table.Column>
  );

  const renderPriceColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        {price.price && (
          <S.PriceColumnWrapper>
            {campaign && <CampaignBadge colors={campaign?.media.colors} />}
            <PriceColumn
              price={price.price}
              storeIsFeatured={featured}
              isCampaign={Boolean(campaign)}
              align="right"
              priceDisplay={priceDisplay}
            />
          </S.PriceColumnWrapper>
        )}
      </Table.Link>
    </Table.Column>
  );

  const renderGoToStoreButton = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        <GoToStoreButton
          visible={Boolean(externalUri || featured)}
          disabled={Boolean(externalUri && !featured)}
          featured={Boolean(featured)}
        />
      </Table.Link>
    </Table.Column>
  );

  const renderOptionsButton = () => (
    <Table.Column>
      <ReportModal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        priceId={price.id}
        priceValue={price.price && price.price.exclShipping}
        priceStore={priceStore}
        productImage={productImage}
        productName={price.name}
        stockStatus={price.stock && price.stock.status}
      />

      <S.Options>
        <Dropdown
          variant="secondary"
          showArrow={false}
          toggleElement={
            <S.OptionsButton>
              <VerticalDots />
            </S.OptionsButton>
          }
          grow="right"
        >
          <S.ReportOverlay>
            <S.Option type="button" onClick={openModal}>
              {t('feedback', 'dialog.prompt-button')}
            </S.Option>
          </S.ReportOverlay>
        </Dropdown>
      </S.Options>
    </Table.Column>
  );

  return (
    <Table.AltRow>
      {renderOptionsButton()}
      {renderProductNameColumn()}
      {renderStockStatusColumn()}
      {renderPriceColumn()}
      {renderGoToStoreButton()}
    </Table.AltRow>
  );
};

export default AlternativePriceRow;
